import React, { useEffect, useState, useRef } from "react";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";
import AudioButton from "@/components/AudioButton";

export default function Messages({ variant = "parent" }) {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (user?.role === "parent") {
      api.get("/parent/children").then(r => { setContacts(r.data); if (r.data[0]) setActive(r.data[0]); });
    } else if (user?.parent_id) {
      // child: fetch parent by parent_id is not exposed; we infer contact via a placeholder
      setContacts([{ id: user.parent_id, name: "Parent" }]);
      setActive({ id: user.parent_id, name: "Parent" });
    }
  }, [user]);

  useEffect(() => {
    if (!active) return;
    api.get(`/messages?with_user=${active.id}`).then(r => setMsgs(r.data));
    const t = setInterval(()=>api.get(`/messages?with_user=${active.id}`).then(r=>setMsgs(r.data)), 4000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(()=>{ ref.current?.scrollTo(0, ref.current.scrollHeight); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    await api.post("/messages", { receiver_id: active.id, text_caption: text });
    setText("");
    const { data } = await api.get(`/messages?with_user=${active.id}`);
    setMsgs(data);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar variant={variant}/>
      <main className="max-w-5xl mx-auto p-4 sm:p-8 grid md:grid-cols-[240px_1fr] gap-6">
        <aside className="parent-card p-4">
          <h2 className="font-display font-bold text-slate-800 mb-3">Contacts</h2>
          {contacts.length === 0 && <p className="text-sm text-slate-500">No contacts.</p>}
          <ul className="space-y-1">
            {contacts.map(c => (
              <li key={c.id}>
                <button onClick={()=>setActive(c)} data-testid={`contact-${c.id}`}
                  className={`w-full text-left px-3 py-2 rounded-lg ${active?.id===c.id?"bg-indigo-100 text-indigo-700":"hover:bg-slate-100"}`}>
                  {c.name}
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <section className="parent-card p-0 flex flex-col h-[70vh]">
          <header className="border-b border-slate-200 p-4 font-display font-bold text-slate-800">
            {active?.name || "Pick a contact"}
          </header>
          <div ref={ref} className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map(m => {
              const mine = m.sender_id === user.id;
              return (
                <div key={m.id} className={`flex ${mine?"justify-end":"justify-start"}`}>
                  <div className={`max-w-xs rounded-2xl px-4 py-2 ${mine?"bg-indigo-600 text-white":"bg-slate-100 text-slate-800"}`}>
                    <p className="text-sm">{m.text_caption}</p>
                    <div className="mt-1 flex items-center justify-between gap-2 text-[10px] opacity-70">
                      <span>{new Date(m.timestamp).toLocaleTimeString()}</span>
                      <AudioButton text={m.text_caption} voice={mine?(user.role==="child"?"child":"parent"):"child"} label="" testId={`play-${m.id}`} className="!py-1 !px-2 !text-xs"/>
                    </div>
                  </div>
                </div>
              );
            })}
            {msgs.length === 0 && <p className="text-center text-slate-400 text-sm">Say hi!</p>}
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2">
            <input value={text} onChange={e=>setText(e.target.value)} placeholder="Type a message..."
              data-testid="msg-input"
              className="flex-1 rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-indigo-500"/>
            <button data-testid="msg-send" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-4">
              <Send className="w-4 h-4"/>
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
